export const HISTORY_LIMIT = 50;

export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
  limit: number;
}

export type HistoryAction<T> =
  | { type: 'commit'; value: T }
  | { type: 'set'; value: T }
  | { type: 'replace'; value: T }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; value: T };

export function createHistoryState<T>(initialValue: T, limit = HISTORY_LIMIT): HistoryState<T> {
  if (!Number.isSafeInteger(limit) || limit < HISTORY_LIMIT) {
    throw new RangeError(`History limit must be an integer of at least ${HISTORY_LIMIT}.`);
  }
  return { past: [], present: initialValue, future: [], limit };
}

export function historyReducer<T>(
  state: HistoryState<T>,
  action: HistoryAction<T>,
): HistoryState<T> {
  if (action.type === 'undo') {
    if (state.past.length === 0) return state;
    const previous = state.past[state.past.length - 1] as T;
    return {
      ...state,
      past: state.past.slice(0, -1),
      present: previous,
      future: [state.present, ...state.future].slice(0, state.limit),
    };
  }

  if (action.type === 'redo') {
    if (state.future.length === 0) return state;
    const next = state.future[0] as T;
    return {
      ...state,
      past: [...state.past, state.present].slice(-state.limit),
      present: next,
      future: state.future.slice(1),
    };
  }

  if (action.type === 'reset') {
    return { ...state, past: [], present: action.value, future: [] };
  }

  if (action.type === 'replace') {
    if (Object.is(action.value, state.present)) return state;
    return { ...state, present: action.value };
  }

  if (Object.is(action.value, state.present)) return state;
  return {
    ...state,
    past: [...state.past, state.present].slice(-state.limit),
    present: action.value,
    future: [],
  };
}

export function canUndo<T>(state: HistoryState<T>): boolean {
  return state.past.length > 0;
}

export function canRedo<T>(state: HistoryState<T>): boolean {
  return state.future.length > 0;
}
